import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Dialog, Select, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Flag, PackageCheck, PackageX, Play, Plus, ScanLine, Truck, Undo2, UserRound, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  needsOverride,
  OperationEventClient,
  ShopFloorClient,
  type ProductionEventKind,
  type ProductionOrderOperationDTO,
  type ScanResultDTO,
  type StepReportRequest,
} from "../../api/floor";
import { CutJobClient } from "../../api/cutJobs";
import { formatNumber, ProcessClient, refusalMessage, SiteClient, StationClient } from "../../api/manufacturing";
import { fetchSingleRow, SETTINGS_PATHS, type ShopFloorSettingsDTO } from "../../api/settings";
import { NexpimView } from "../../views";
import { materialsUsed, overEstimate, PanelsUsed, stepMaterialsKey, useCountReasonPrompt, useStepMaterials, type PendingCounts } from "./PanelsUsed";
import { useOverridePrompt } from "../production/OverridePrompt";
import { CutJobList, CutJobStation } from "./CutJobStation";
import { usePartFiles, ViewDocumentButton } from "./DocumentPreview";
import { BigButton, Figure, QuantityDialog, ScrapDialog } from "./kioskParts";
import { StationQueue } from "./StationQueue";
import { byPosition, partsPerUnit, processLabel, StepChip } from "./steps";

const STATION_KEY = "nexpim.kiosk.station";
/** A lot or component code: 900123-4, a traveller 900123-4.2, or a bare number (a SIGM child project or a lot). */
const LOT_CODE = /^\*?\d+(-\d+(\.\d+)?)?\*?$/;

interface Operator {
  badge: string;
  name: string;
  /** This badge may go past what a step allows, with a reason. */
  overrideRight: boolean;
  /** Last report or badge scan, ms: the badge is forgotten after the settings' idle time. */
  active: number;
}

interface Flash {
  tone: "ok" | "error";
  text: string;
}

/** One remembered station per site: the Laval and Terrebonne kiosks are different devices. */
function stationKey(site: string | undefined): string {
  return site ? `${STATION_KEY}.${site}` : STATION_KEY;
}

function storedStation(site: string | undefined): number | null {
  try {
    // The kiosks set before there was one per site remember theirs under the plain key.
    const value = window.localStorage.getItem(stationKey(site)) ?? window.localStorage.getItem(STATION_KEY);
    return value ? Number(value) : null;
  } catch {
    return null;
  }
}

function storeStation(site: string | undefined, id: number | null) {
  try {
    if (id == null) window.localStorage.removeItem(stationKey(site));
    else window.localStorage.setItem(stationKey(site), String(id));
  } catch {
    // Blocked storage: the station is asked again next time.
  }
}

/**
 * A station kiosk: the touchscreen beside a machine, with a USB scanner that
 * types what it reads followed by Enter.
 *
 * <p>The device is set to its station once (remembered in this browser). The
 * scan field keeps the focus, so the operator only scans: a traveller or a
 * component code opens that part at this station's step; anything else is
 * tried as a badge, which names who is working until it has been idle for the
 * shop-floor settings' minutes. Nobody has to log in to report: without a
 * badge the work is recorded against the station only.</p>
 *
 * <p>Reports are the server's step events: start, a good quantity (pre-filled
 * with what is left of what the steps it waits for have passed), scrap with a
 * reason, missing, finish. Going past what is allowed needs a reason and a
 * badge allowed to override (a lead's, scanned in the dialog when the badge in
 * use cannot); it is recorded with who and by how much. Paint is done outside:
 * the painter's step is reported as sent, then back. The last report from this
 * kiosk can be undone, which reports the opposite quantity: the log is never
 * edited.</p>
 */
export function StationKioskPage({ site }: { site?: string }) {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.SHOP_FLOOR_STATION);
  const floor = useMemo(() => new ShopFloorClient(http), [http]);
  const [stationId, setStationId] = useState<number | null>(() => storedStation(site));
  const [choosing, setChoosing] = useState(stationId == null);
  const [operator, setOperator] = useState<Operator | null>(null);
  const [scan, setScan] = useState<ScanResultDTO | null>(null);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  // A STOCK station (the saw) works from cut jobs: the one open, if any (docs/cutting-jobs §7).
  const [jobId, setJobId] = useState<number | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [dialog, setDialog] = useState<null | "report" | "send" | "scrap" | "missing" | "finish">(null);
  const [lastReport, setLastReport] = useState<{ operationId: number; kind: "REPORT" | "SEND"; amount: number; label: string } | null>(null);
  // Panels counted at the saw, waiting to go with the next report (or alone, with Count).
  const [panels, setPanels] = useState<PendingCounts>({});
  const [code, setCode] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const override = useOverridePrompt(!operator?.overrideRight);
  // A count past its estimate says why before it is sent (the server refuses it otherwise).
  const countReason = useCountReasonPrompt();
  const stepMaterials = useStepMaterials(selectedStep);
  const reasonForCounts = async (): Promise<string | null | undefined> => {
    const over = overEstimate(stepMaterials.data ?? [], panels);
    return over.length === 0 ? undefined : countReason.ask(over);
  };

  const allStations = useQuery({
    queryKey: ["station", "all"],
    queryFn: () => new StationClient(http).findAll(),
    enabled: allowed,
    staleTime: 10 * 60_000,
  });
  const sites = useQuery({
    queryKey: ["site", "all"],
    queryFn: () => new SiteClient(http).findAll(),
    enabled: allowed && site != null,
    staleTime: 10 * 60_000,
  });
  // A site's kiosk (Fabrication at Laval, Assembly at Terrebonne) offers only its stations.
  const siteId = site == null ? null : (sites.data ?? []).find((row) => row.code === site)?.id ?? null;
  const stations = {
    ...allStations,
    data: site == null ? allStations.data : siteId == null ? [] : (allStations.data ?? []).filter((row) => row.site_id === siteId),
  };
  const settings = useQuery({
    queryKey: ["settings", "shop-floor"],
    queryFn: () => fetchSingleRow<ShopFloorSettingsDTO>(http, SETTINGS_PATHS.shopFloor),
    enabled: allowed,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const refreshMs = (settings.data?.board_refresh_seconds ?? 60) * 1000;
  const idleMinutes = settings.data?.operator_idle_minutes ?? 30;
  const station = (stations.data ?? []).find((row) => row.id === stationId) ?? null;
  const processes = useQuery({
    queryKey: ["process", "all"],
    queryFn: () => new ProcessClient(http).findAll(),
    enabled: allowed,
    staleTime: 10 * 60_000,
  });
  const worksOnStock = (processes.data ?? []).find((row) => row.id === station?.process_id)?.works_on === "STOCK";
  const cutJobs = useQuery({
    queryKey: ["production", "cut-job", "station", stationId],
    queryFn: () => new CutJobClient(http).forStation(stationId!),
    enabled: allowed && stationId != null && worksOnStock,
    refetchInterval: refreshMs,
  });

  const queue = useQuery({
    queryKey: ["production", "shop-floor", "queue", stationId],
    queryFn: () => floor.queue(stationId!),
    // A saw's work is its cut jobs: it has no queue of pieces.
    enabled: allowed && stationId != null && !worksOnStock,
    refetchInterval: refreshMs,
  });
  const recent = useQuery({
    queryKey: ["production", "shop-floor", "station-events", stationId],
    queryFn: () => new OperationEventClient(http).forStation(stationId!, 10),
    enabled: allowed && stationId != null,
    refetchInterval: refreshMs,
  });
  // What the scanned work is made from: a part's drawing, an assembly order's product's assembly plan.
  const assembling = scan?.lot.kind === "ASSEMBLY";
  const drawing = usePartFiles({ partId: assembling ? null : (scan?.component?.part_id ?? null) }, ["DRAWING"]);
  const assemblyPlan = usePartFiles({ productId: assembling ? (scan?.lot.product_id ?? null) : null }, ["EXPLODED_VIEW", "ASSEMBLY_GUIDE"]);

  // Messages fade: errors stay a little longer, since the operator may be across the machine.
  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), flash.tone === "error" ? 7000 : 3000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // A badge is forgotten after the settings' idle time without a report.
  useEffect(() => {
    if (!operator) return;
    const timer = window.setInterval(() => {
      if (Date.now() - operator.active > idleMinutes * 60_000) setOperator(null);
    }, 20_000);
    return () => window.clearInterval(timer);
  }, [operator, idleMinutes]);

  // The scan field keeps the focus unless a dialog or the station picker has it.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const active = document.activeElement;
      if (dialog || choosing || stationId == null) return;
      if (active && active !== input.current && active.closest("dialog")) return;
      if (active !== input.current) input.current?.focus({ preventScroll: true });
    }, 700);
    return () => window.clearInterval(timer);
  }, [dialog, choosing, stationId]);

  const showError = (failure: unknown, fallback: string) => setFlash({ tone: "error", text: refusalMessage(failure, fallback) });

  const openCode = useCallback(
    async (raw: string) => {
      const value = raw.trim();
      if (value === "") return;
      const looksLikeLot = LOT_CODE.test(value);
      const tryScan = async () => {
        const result = await floor.scan(value, stationId);
        setJobId(null);
        setScan(result);
        setSelectedStep(result.station_operation_id ?? null);
        setPanels({});
        return true;
      };
      const tryBadge = async () => {
        const found = await floor.badge(value);
        setOperator({ badge: found.badge_code, name: found.name, overrideRight: Boolean(found.has_override_right), active: Date.now() });
        setFlash({ tone: "ok", text: t("kiosk.badge.hello", "Hello {{name}}", { name: found.name }) });
        return true;
      };
      try {
        await (looksLikeLot ? tryScan() : tryBadge());
      } catch (first) {
        try {
          await (looksLikeLot ? tryBadge() : tryScan());
        } catch {
          showError(first, t("kiosk.scan.unknown", "That code is not a drawing, a part on the floor or a badge."));
        }
      }
    },
    [floor, stationId, t],
  );

  const replaceStep = (updated: ProductionOrderOperationDTO) =>
    setScan((current) =>
      current ? { ...current, operations: current.operations.map((step) => (step.id === updated.id ? { ...step, ...updated } : step)) } : current,
    );

  const report = useMutation({
    mutationFn: async ({ step, request }: { step: ProductionOrderOperationDTO; request: StepReportRequest }) => {
      const counted = request.kind === "REPORT" && request.note !== "Undo" ? materialsUsed(panels) : [];
      const why = counted.length > 0 ? await reasonForCounts() : undefined;
      if (why === null) return null;
      const full: StepReportRequest = {
        ...request,
        station_id: stationId,
        operator_badge: operator?.badge ?? null,
        pallet_number: scan?.pallet_number ?? null,
        ...(counted.length > 0 ? { materials_used: counted } : {}),
        ...(why ? { materials_reason: why } : {}),
      };
      try {
        return await floor.report(step.id, full);
      } catch (failure) {
        const needed = needsOverride(failure);
        if (needed == null) throw failure;
        const answer = await override.ask(needed);
        if (!answer) return null;
        return floor.report(step.id, { ...full, override_reason: answer.reason, operator_badge: answer.badge ?? full.operator_badge });
      }
    },
    onSuccess: (updated, { request }) => {
      if (!updated) return;
      replaceStep(updated);
      if (request.kind === "REPORT" && request.note !== "Undo" && materialsUsed(panels).length > 0) {
        setPanels({});
        void queryClient.invalidateQueries({ queryKey: stepMaterialsKey(updated.id) });
      }
      if (operator) setOperator({ ...operator, active: Date.now() });
      const amount = request.kind === "SEND" ? Number(request.quantity_sent ?? 0) : Number(request.quantity_good ?? 0);
      if ((request.kind === "REPORT" || request.kind === "SEND") && amount !== 0 && request.note !== "Undo") {
        setLastReport({
          operationId: updated.id,
          kind: request.kind,
          amount,
          label: `${updated.component_code ?? ""} ${processLabel(t, updated.process_code, updated.process_name)}`,
        });
      }
      setFlash({ tone: "ok", text: reportedText(t, request) });
      void queryClient.invalidateQueries({ queryKey: ["production", "shop-floor"] });
    },
    onError: (failure) => showError(failure, t("error.generic", "Something went wrong.")),
  });

  // Raw stock counted alone, without a report (counted later than the parts).
  const count = useMutation({
    mutationFn: async (operationId: number) => {
      const why = await reasonForCounts();
      if (why === null) return null;
      return floor.count(operationId, {
        materials_used: materialsUsed(panels),
        station_id: stationId,
        operator_badge: operator?.badge ?? null,
        reason: why ?? null,
      });
    },
    onSuccess: (result, operationId) => {
      if (result === null) return;
      setPanels({});
      void queryClient.invalidateQueries({ queryKey: stepMaterialsKey(operationId) });
      setFlash({ tone: "ok", text: t("kiosk.panels.counted", "Panels counted") });
    },
    onError: (failure) => showError(failure, t("error.generic", "Something went wrong.")),
  });

  const send = (kind: ProductionEventKind, extra: Partial<StepReportRequest> = {}) => {
    if (!step) return;
    report.mutate({ step, request: { kind, ...extra } });
  };

  if (!allowed) {
    return (
      <div className="p-6">
        <p className="text-[15px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
      </div>
    );
  }

  const steps = scan ? byPosition(scan.operations) : [];
  // Only this station's own steps are reported here.
  const mineCount = steps.filter((row) => station?.process_id != null && row.process_id === station.process_id).length;
  const step = steps.find((row) => row.id === selectedStep && station?.process_id != null && row.process_id === station.process_id) ?? null;
  const factor = step ? partsPerUnit(step, scan?.component) : 1;
  const required = step ? Number(step.quantity_required_counted ?? step.quantity_required) : 0;
  const done = step ? Number(step.quantity_done ?? 0) : 0;
  const availableCounted = step ? Math.ceil(Number(step.quantity_available ?? step.quantity_required) / factor) : 0;
  const external = Boolean(step?.is_external);
  const sent = step ? Number(step.quantity_sent ?? 0) : 0;
  const scrapped = step ? Number(step.quantity_scrap ?? 0) : 0;
  // What is left: of what may come through, or, back from the painter, of what was sent.
  const remaining = external ? Math.max(0, sent - done - scrapped) : Math.max(0, Math.min(availableCounted, required) - done);
  const toSend = Math.max(0, Math.min(availableCounted, required) - sent);
  // What is left to make (or, at the painter, to send or to get back), whatever the step before has passed.
  const leftToMake = external ? Math.max(0, sent - done - scrapped) : Math.max(0, required - done);
  const leftToSend = Math.max(0, required - sent);
  const finished = step?.status === "DONE" || step?.status === "SKIPPED";
  const unit = step?.counting_unit === "CUTBOARD" && factor > 1 ? t("kiosk.unit.boards", "boards") : t("kiosk.unit.parts", "parts");

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto bg-surface-sunk">
      {/* Top bar: station, operator, scan field. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-3 lg:px-6">
        <button
          type="button"
          onClick={() => setChoosing(true)}
          className="flex min-h-12 items-center gap-2 rounded-lg border border-line px-4 text-left hover:bg-surface-sunk"
        >
          <span className="text-[12px] text-ink-muted">{t("kiosk.station", "Station")}</span>
          <span className="text-[20px] font-semibold">{station?.name ?? t("kiosk.station.none", "Choose")}</span>
        </button>
        <div className={cn("flex min-h-12 items-center gap-2 rounded-lg border px-4", operator ? "border-ok/50 bg-ok-soft" : "border-line")}>
          <UserRound className="size-5 text-ink-muted" />
          <span className="text-[18px] font-medium">{operator?.name ?? t("kiosk.operator.anonymous", "No badge")}</span>
          {operator && (
            <button type="button" className="ml-2 text-[13px] text-ink-muted underline" onClick={() => setOperator(null)}>
              {t("kiosk.operator.leave", "Sign out")}
            </button>
          )}
        </div>
        <form
          className="flex min-w-[260px] flex-1 items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const value = code;
            setCode("");
            void openCode(value);
          }}
        >
          <ScanLine className="size-6 text-ink-muted" />
          <input
            ref={input}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoFocus
            disabled={stationId == null}
            autoComplete="off"
            inputMode="none"
            aria-label={t("kiosk.scan", "Scan the drawing")}
            placeholder={t("kiosk.scan", "Scan the drawing")}
            className="h-12 w-full rounded-lg border-2 border-accent bg-surface px-4 text-[20px] outline-none"
          />
        </form>
      </div>

      {flash && (
        <div
          role="status"
          className={cn(
            "mx-4 mt-3 flex items-center gap-3 rounded-lg px-5 py-4 text-[20px] font-semibold lg:mx-6",
            flash.tone === "ok" ? "bg-ok-soft text-ok" : "bg-risk-soft text-risk",
          )}
        >
          {flash.tone === "ok" ? <CheckCircle2 className="size-7" /> : <XCircle className="size-7" />}
          {flash.text}
        </div>
      )}

      {stationId == null ? (
        // No station yet: nothing can be reported until this device says where it is.
        <div className="m-4 flex flex-col items-center gap-4 rounded-xl border-2 border-warn/50 bg-warn-soft px-6 py-14 text-center lg:m-6">
          <ScanLine className="size-14 text-warn" />
          <p className="text-[22px] font-semibold text-warn">{t("kiosk.station.missing", "This device has no station yet")}</p>
          <p className="max-w-xl text-[16px] text-ink-muted">
            {t("kiosk.station.missing.hint", "Choose the machine this screen is beside: its list of work and every report made here go by it.")}
          </p>
          <Button variant="primary" onClick={() => setChoosing(true)}>
            {t("kiosk.station.choose", "Choose the station")}
          </Button>
        </div>
      ) : (
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:p-6">
        {/* The scanned part at this station, or the cut job open at the saw. */}
        <section className="rounded-xl border border-line bg-surface p-5">
          {jobId != null && stationId != null ? (
            <CutJobStation
              key={jobId}
              jobId={jobId}
              stationId={stationId}
              operatorBadge={operator?.badge ?? null}
              onReport={(target, request) => report.mutateAsync({ step: target, request })}
              onFlash={(tone, text) => setFlash({ tone, text })}
              onClose={() => setJobId(null)}
            />
          ) : !scan ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-ink-muted">
              <ScanLine className="size-14" />
              <p className="text-[22px] font-medium">{t("kiosk.scan", "Scan the drawing")}</p>
              <p className="text-[15px]">{t("kiosk.scan.hint", "Or tap a part in the list to work on it.")}</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                <span className="text-[34px] font-bold tabular-nums">{scan.component?.code ?? scan.lot.number}</span>
                {scan.component?.letter && <span className="rounded-md border-2 border-ink px-3 text-[30px] font-bold">{scan.component.letter}</span>}
                {scan.pallet_number != null && (
                  <span className="text-[18px] text-ink-muted">{t("kiosk.pallet", "Pallet {{n}}", { n: scan.pallet_number })}</span>
                )}
              </div>
              <p className="mt-1 text-[19px]">
                <span className="font-semibold">{scan.component?.part_number ?? scan.lot.part_number}</span>
                <span className="ml-2 text-ink-muted">{scan.component?.part_name ?? scan.lot.part_name}</span>
              </p>
              <p className="text-[15px] text-ink-muted">
                {t("kiosk.lot", "Lot {{n}}", { n: scan.lot.number })} · {scan.lot.part_number ?? scan.lot.name}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {assembling ? (
                  <ViewDocumentButton label={t("kiosk.view.assembly", "View assembly plan")} documents={assemblyPlan.documents} loading={assemblyPlan.loading} />
                ) : (
                  <ViewDocumentButton label={t("kiosk.view.part", "View part")} documents={drawing.documents} loading={drawing.loading} />
                )}
              </div>

              {/* The part's steps, for context. The one reported here is this
                  station's, picked by its machine; when the part has several of
                  this machine's steps, those (and only those) can be switched. */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {steps.map((row) => {
                  const mine = station?.process_id != null && row.process_id === station.process_id;
                  const current = row.id === selectedStep;
                  const chip = (
                    <span className={cn("inline-flex items-center gap-2 rounded-full", current && "ring-2 ring-accent ring-offset-2", !mine && "opacity-60")}>
                      <StepChip step={row} large />
                      {current && (
                        <span className="rounded-full bg-accent px-2 py-0.5 text-[12px] font-semibold text-ink-on-accent">
                          {t("kiosk.step.here", "This station")}
                        </span>
                      )}
                    </span>
                  );
                  return mine && mineCount > 1 && !current ? (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => {
                        setSelectedStep(row.id);
                        setPanels({});
                      }}
                    >
                      {chip}
                    </button>
                  ) : (
                    <span key={row.id}>{chip}</span>
                  );
                })}
              </div>

              {!step ? (
                <p className="mt-6 rounded-lg bg-warn-soft px-4 py-3 text-[17px] text-warn">
                  {t("kiosk.no-step.here", "This station does no step of this part: report it at the station that does.")}
                </p>
              ) : (
                <>
                  <h2 className="mt-6 text-[24px] font-semibold">
                    {processLabel(t, step.process_code, step.process_name)}
                    {step.program_code && <span className="ml-3 text-[18px] font-normal text-ink-muted">{t("kiosk.program", "Program {{p}}", { p: step.program_code })}</span>}
                  </h2>
                  {step.note && <p className="mt-1 text-[16px] text-warn">{step.note}</p>}
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <Figure label={t("kiosk.required", "To make")} value={required} unit={unit} />
                    {external ? (
                      <>
                        <Figure label={t("kiosk.sent", "Sent to painter")} value={sent} unit={unit} />
                        <Figure label={t("kiosk.back", "Back from painter")} value={done} unit={unit} strong />
                      </>
                    ) : (
                      <>
                        <Figure label={t("kiosk.done", "Done")} value={done} unit={unit} strong />
                        <Figure label={t("kiosk.passed", "Passed by the step before")} value={availableCounted} unit={unit} />
                      </>
                    )}
                    <Figure label={t("kiosk.scrap", "Scrap")} value={Number(step.quantity_scrap ?? 0)} tone={Number(step.quantity_scrap ?? 0) > 0 ? "warn" : undefined} />
                    <Figure label={t("kiosk.missing", "Missing")} value={Number(step.quantity_missing ?? 0)} tone={Number(step.quantity_missing ?? 0) > 0 ? "risk" : undefined} />
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <BigButton icon={<Play className="size-6" />} label={t("kiosk.start", "Start")} disabled={finished || report.isPending || step.status === "ACTIVE"} onClick={() => send("START")} />
                    {external ? (
                      <>
                        <BigButton icon={<Truck className="size-6" />} label={t("kiosk.send", "Sent to painter")} disabled={report.isPending} onClick={() => setDialog("send")} />
                        <BigButton icon={<PackageCheck className="size-6" />} label={t("kiosk.back", "Back from painter")} primary disabled={report.isPending} onClick={() => setDialog("report")} />
                      </>
                    ) : (
                      <BigButton icon={<Plus className="size-6" />} label={t("kiosk.report", "Report")} primary disabled={report.isPending} onClick={() => setDialog("report")} />
                    )}
                    <BigButton icon={<PackageX className="size-6" />} label={t("kiosk.scrap", "Scrap")} disabled={report.isPending} onClick={() => setDialog("scrap")} />
                    <BigButton icon={<Flag className="size-6" />} label={t("kiosk.missing", "Missing")} disabled={report.isPending} onClick={() => setDialog("missing")} />
                    {finished ? (
                      <BigButton icon={<Undo2 className="size-6" />} label={t("kiosk.reopen", "Reopen")} disabled={report.isPending} onClick={() => send("REOPEN")} />
                    ) : (
                      <BigButton icon={<CheckCircle2 className="size-6" />} label={t("kiosk.finish", "Finish")} disabled={report.isPending}
                        // What is made now is reported with the finish; with nothing left, it just finishes.
                        onClick={() => (leftToMake > 0 ? setDialog("finish") : send("FINISH"))} />
                    )}
                    <BigButton
                      icon={<Undo2 className="size-6" />}
                      label={t("kiosk.undo", "Undo last")}
                      disabled={report.isPending || lastReport == null}
                      onClick={async () => {
                        if (!lastReport) return;
                        const target = steps.find((row) => row.id === lastReport.operationId);
                        if (!target) {
                          setFlash({ tone: "error", text: t("kiosk.undo.elsewhere", "Scan {{what}} again to undo it.", { what: lastReport.label }) });
                          return;
                        }
                        const yes = await confirm({
                          title: t("kiosk.undo.title", "Undo the last report?"),
                          message: t("kiosk.undo.message", "{{n}} on {{what}} is taken back.", { n: formatNumber(lastReport.amount), what: lastReport.label }),
                          confirmLabel: t("kiosk.undo", "Undo last"),
                          variant: "danger",
                        });
                        if (!yes) return;
                        report.mutate({
                          step: target,
                          request:
                            lastReport.kind === "SEND"
                              ? { kind: "SEND", quantity_sent: -lastReport.amount, note: "Undo" }
                              : { kind: "REPORT", quantity_good: -lastReport.amount, note: "Undo" },
                        });
                        setLastReport(null);
                      }}
                    />
                  </div>
                  {!external && (
                    <PanelsUsed
                      key={step.id}
                      operationId={step.id}
                      processCode={step.process_code}
                      pending={panels}
                      onChange={setPanels}
                      onCount={() => count.mutate(step.id)}
                      busy={report.isPending || count.isPending}
                    />
                  )}
                </>
              )}
            </>
          )}
        </section>

        {/* What this station has to do, and what it last did. */}
        <aside className="flex min-w-0 flex-col gap-4">
          {worksOnStock && (
            <section className="rounded-xl border border-line bg-surface">
              <h2 className="border-b border-line px-4 py-3 text-[17px] font-semibold">{t("kiosk.jobs", "Cut jobs")}</h2>
              <CutJobList jobs={cutJobs.data ?? []} current={jobId} onPick={(id) => {
                setJobId(id);
                setScan(null);
                setSelectedStep(null);
              }} />
            </section>
          )}
          {!worksOnStock && (
            <section className="rounded-xl border border-line bg-surface">
              <h2 className="border-b border-line px-4 py-3 text-[17px] font-semibold">{t("kiosk.queue", "To do here")}</h2>
              <StationQueue
                rows={queue.data ?? []}
                stationId={stationId}
                operatorBadge={operator?.badge ?? null}
                // An assembly order's steps belong to no component: its number opens them.
                onOpen={(row) => void openCode(row.component_code ?? String(row.production_order_number))}
                onDone={(results) => {
                  const refused = results.filter((result) => !result.ok);
                  void queryClient.invalidateQueries({ queryKey: ["production", "shop-floor"] });
                  setFlash(refused.length === 0
                    ? { tone: "ok", text: t("kiosk.bulk.done", "{{n}} steps done", { n: results.length }) }
                    : { tone: "error", text: t("kiosk.bulk.refused", "{{n}} not done: {{why}}", { n: refused.length, why: refused[0]?.message ?? "" }) });
                }}
              />
            </section>
          )}
          <section className="rounded-xl border border-line bg-surface">
            <h2 className="border-b border-line px-4 py-3 text-[17px] font-semibold">{t("kiosk.recent", "Last reports here")}</h2>
            <ul className="flex flex-col">
              {(recent.data ?? []).length === 0 ? (
                <li className="px-4 py-4 text-[14px] text-ink-muted">{t("kiosk.recent.empty", "Nothing reported yet.")}</li>
              ) : (
                (recent.data ?? []).map((event) => (
                  <li key={event.id} className="flex items-center gap-2 border-b border-line px-4 py-2 text-[14px] last:border-b-0">
                    <span className="tabular-nums text-ink-muted">
                      {new Date(event.recorded_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className="font-semibold tabular-nums">{event.component_code}</span>
                    <span className="min-w-0 flex-1 truncate">{eventText(t, event.kind, Number(event.quantity_good), Number(event.quantity_scrap), event.quantity_missing)}</span>
                    {event.operator_name && <span className="text-ink-muted">{event.operator_name}</span>}
                  </li>
                ))
              )}
            </ul>
          </section>
        </aside>
      </div>
      )}

      {choosing && (
        <StationPicker
          stations={(stations.data ?? []).filter((row) => row.is_active && row.process_id != null)}
          current={stationId}
          onPick={(id) => {
            setStationId(id);
            storeStation(site, id);
            setChoosing(false);
            setScan(null);
          }}
          onClose={() => setChoosing(false)}
        />
      )}
      {dialog === "send" && step && (
        <QuantityDialog
          title={t("kiosk.send.title", "{{unit}} sent to the painter", { unit })}
          initial={leftToSend}
          max={leftToSend}
          hint={toSend < leftToSend ? t("kiosk.passed.hint", "The step before has passed {{n}}: more than that asks for a lead's badge.", { n: formatNumber(availableCounted) }) : undefined}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            setDialog(null);
            if (value !== 0) send("SEND", { quantity_sent: value });
          }}
        />
      )}
      {dialog === "report" && step && (
        <QuantityDialog
          title={external ? t("kiosk.back.title", "Good {{unit}} back from the painter", { unit }) : t("kiosk.report.title", "Good {{unit}} done", { unit })}
          initial={leftToMake}
          max={leftToMake}
          hint={!external && remaining < leftToMake ? t("kiosk.passed.hint", "The step before has passed {{n}}: more than that asks for a lead's badge.", { n: formatNumber(availableCounted) }) : undefined}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            setDialog(null);
            if (value !== 0) send("REPORT", { quantity_good: value });
          }}
        />
      )}
      {dialog === "finish" && step && (
        <QuantityDialog
          title={external ? t("kiosk.finish.back.title", "Finish: good {{unit}} back from the painter", { unit }) : t("kiosk.finish.title", "Finish: good {{unit}} done now", { unit })}
          initial={leftToMake}
          max={leftToMake}
          hint={t("kiosk.finish.hint", "Reported with the finish. 0 finishes without reporting more.")}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            setDialog(null);
            if (value <= 0) {
              send("FINISH");
              return;
            }
            // Reported as its own report first (its panels, a lead's badge if it goes past what
            // came through), then finished; reporting all that was left finishes it already.
            report.mutate(
              { step, request: { kind: "REPORT", quantity_good: value } },
              {
                onSuccess: (updated) => {
                  if (updated && updated.status !== "DONE") report.mutate({ step: updated, request: { kind: "FINISH" } });
                },
              },
            );
          }}
        />
      )}
      {dialog === "missing" && step && (
        <QuantityDialog
          title={t("kiosk.missing.title", "How many are missing now")}
          hint={t("kiosk.missing.hint", "0 clears the count.")}
          initial={Number(step.quantity_missing ?? 0)}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            setDialog(null);
            send("MISSING", { quantity_missing: value });
          }}
        />
      )}
      {dialog === "scrap" && step && (
        <ScrapDialog
          onClose={() => setDialog(null)}
          onSubmit={(quantity, reasonId) => {
            setDialog(null);
            send("REPORT", { quantity_good: 0, quantity_scrap: quantity, production_scrap_reason_id: reasonId });
          }}
        />
      )}
      {override.element}
      {countReason.element}
    </div>
  );
}

function reportedText(t: (key: string, fallback: string, options?: Record<string, unknown>) => string, request: StepReportRequest): string {
  switch (request.kind) {
    case "START":
      return t("kiosk.flash.started", "Started");
    case "FINISH":
      return t("kiosk.flash.finished", "Step finished");
    case "REOPEN":
      return t("kiosk.flash.reopened", "Step reopened");
    case "MISSING":
      return t("kiosk.flash.missing", "Missing count saved");
    case "SEND":
      return t("kiosk.flash.sent", "{{n}} sent to the painter", { n: formatNumber(request.quantity_sent) });
    default:
      return Number(request.quantity_scrap ?? 0) > 0
        ? t("kiosk.flash.scrap", "{{n}} scrapped", { n: formatNumber(request.quantity_scrap) })
        : t("kiosk.flash.reported", "{{n}} reported", { n: formatNumber(request.quantity_good) });
  }
}

function eventText(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  kind: ProductionEventKind,
  good: number,
  scrap: number,
  missing: number | null,
): string {
  switch (kind) {
    case "START":
      return t("kiosk.event.start", "started");
    case "FINISH":
      return t("kiosk.event.finish", "finished");
    case "SKIP":
      return t("kiosk.event.skip", "skipped");
    case "REOPEN":
      return t("kiosk.event.reopen", "reopened");
    case "MISSING":
      return t("kiosk.event.missing", "{{n}} missing", { n: formatNumber(missing) });
    case "SEND":
      return t("kiosk.event.sent", "sent out");
    default:
      return [good !== 0 ? t("kiosk.event.good", "{{n}} good", { n: formatNumber(good) }) : null, scrap > 0 ? t("kiosk.event.scrap", "{{n}} scrap", { n: formatNumber(scrap) }) : null]
        .filter(Boolean)
        .join(", ");
  }
}

function StationPicker({
  stations,
  current,
  onPick,
  onClose,
}: {
  stations: Array<{ id: number; code: string; name: string }>;
  current: number | null;
  onPick: (id: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState(current == null ? "" : String(current));
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("kiosk.station.title", "Which station is this?")}
      description={t("kiosk.station.description", "Set once on this device; reports from here are recorded at this station.")}
      actions={
        <Button variant="primary" disabled={chosen === ""} onClick={() => onPick(Number(chosen))}>
          {t("kiosk.station.use", "Use this station")}
        </Button>
      }
    >
      <Select
        value={chosen}
        placeholder={t("form.choose", "Choose…")}
        options={stations.map((station) => ({ value: String(station.id), label: `${station.name} (${station.code})` }))}
        onChange={(event) => setChosen(event.target.value)}
      />
    </Dialog>
  );
}

